/**
 * Display labels for key bindings, read from the player's live bindings and
 * never hard-coded (modular action bars plan, phase 3). NPC text, the bar's
 * slot badges and world prompts (the chest's "[E] Open") all use these.
 */
import type { KeyBinding } from '../../../shared/CharacterTypes';
import { slotAction } from '../../../shared/hotbar/slotActions';

const NAMED_KEYS: Record<string, string> = {
  SPACE: 'Space', ESCAPE: 'Esc', TAB: 'Tab', ALT: 'Alt', SHIFT: 'Shift', CTRL: 'Ctrl', ENTER: 'Enter',
  BACKSPACE: 'Backspace', LEFT: '←', RIGHT: '→', UP: '↑', DOWN: '↓', NUMLOCK: 'NumLock', BACKTICK: '`',
};
const SHORT_KEYS: Record<string, string> = {
  SPACE: 'Spc', ESCAPE: 'Esc', ENTER: 'Ent', NUMLOCK: 'NL', BACKSPACE: 'Bk',
};
const NUMPAD = ['ZERO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE'];

const numpadDigit = (key: string) => (key.startsWith('NUMPAD_') ? NUMPAD.indexOf(key.slice('NUMPAD_'.length)) : -1);

/** "I", "Space", "Ctrl+S", "Num 1". */
export function formatKeyBinding(binding: Pick<KeyBinding, 'key' | 'modifier'>): string {
  const key = binding.key.toUpperCase();
  const numpad = numpadDigit(key);
  const label = numpad >= 0 ? `Num ${numpad}` : NAMED_KEYS[key] ?? (key.length === 1 ? key : key.charAt(0) + key.slice(1).toLowerCase());
  const modifier = binding.modifier ? `${binding.modifier.charAt(0).toUpperCase()}${binding.modifier.slice(1)}+` : '';
  return modifier + label;
}

/**
 * The short form for a bar slot's badge: "Q", "s1" (Shift+1), "c3", "a0",
 * "N1" (numpad 1), "Spc". MMO convention: a lower-case modifier initial.
 */
export function shortKeyLabel(binding: Pick<KeyBinding, 'key' | 'modifier'>): string {
  const key = binding.key.toUpperCase();
  const numpad = numpadDigit(key);
  const label = numpad >= 0 ? `N${numpad}` : SHORT_KEYS[key] ?? NAMED_KEYS[key] ?? key;
  return (binding.modifier ? binding.modifier.charAt(0) : '') + label;
}

/** An action's first binding, formatted; undefined when nothing is bound. */
export function actionKeyLabel(bindings: readonly KeyBinding[], action: string): string | undefined {
  const binding = bindings.find(b => b.action === action);
  return binding ? formatKeyBinding(binding) : undefined;
}

/** A bar slot's badge: its first bound key, or '' when the slot is unbound. */
export function slotKeyLabel(bindings: readonly KeyBinding[], bar: number, slot: number): string {
  const binding = bindings.find(b => b.action === slotAction(bar, slot));
  return binding ? shortKeyLabel(binding) : '';
}
